import { useState } from "react";
import { Mail, MapPin } from "lucide-react";
import Reveal from "@/components/Reveal";
import Eyebrow from "@/components/Eyebrow";
import { api } from "@/lib/api";
import { toast } from "sonner";

const heroImg =
  "https://images.unsplash.com/photo-1758599543152-a73184816eba?crop=entropy&cs=srgb&fm=jpg&w=2000&q=85";

const initialForm = {
  name: "", company: "", role: "", email: "", phone: "",
  region: "", track: "", business_brief: "", other_notes: "",
};

const Contact = () => {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/agency-applications", form);
      setSuccess(true);
      setForm(initialForm);
      toast.success("Application received.", { description: "We'll be in touch within 5 working days." });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Please check your details and try again.";
      toast.error("Could not submit application.", { description: typeof detail === "string" ? detail : "Validation error." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* HERO */}
      <section className="relative min-h-[80vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroImg} alt="A handshake beginning a partnership conversation" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/30 mix-blend-multiply" />
        </div>
        <div className="relative container-x pb-20 md:pb-28 pt-32">
          <div className="max-w-3xl">
            <Eyebrow color="ochre" className="mb-8">
              <span className="text-ecospora-ochre">Get in touch</span>
            </Eyebrow>
            <h1 className="font-display text-5xl md:text-6xl lg:text-[72px] font-bold leading-[1.05] tracking-tight text-ecospora-cream text-balance">
              Let's grow something <span className="italic font-light text-ecospora-green-soft">together.</span>
            </h1>
            <p className="mt-8 text-lg md:text-xl text-ecospora-cream/85 max-w-2xl leading-relaxed">
              Partners, agencies, researchers, and anyone building something with mushrooms. We want to hear from you.
            </p>
            <a href="#form" className="btn-ochre mt-10 inline-flex" data-testid="contact-hero-cta">
              Go to the form ↓
            </a>
          </div>
        </div>
      </section>

      {/* CONTACT DETAILS */}
      <section className="section-y bg-ecospora-cream">
        <div className="container-x">
          <Reveal className="grid md:grid-cols-12 gap-10 items-start">
            <div className="md:col-span-5">
              <Eyebrow className="mb-6">Reach Us</Eyebrow>
              <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-ecospora-green-deep leading-[1.05]">
                Every good partnership <span className="italic font-light">starts with a conversation.</span>
              </h2>
            </div>
            <div className="md:col-span-7 md:pt-4 space-y-6">
              <a
                href="mailto:hello@ecospora.com"
                className="flex items-center gap-4 text-lg text-ecospora-charcoal hover:text-ecospora-green-deep transition"
                data-testid="contact-email-link"
              >
                <span className="w-12 h-12 rounded-full bg-ecospora-white border border-ecospora-green-deep/15 flex items-center justify-center shrink-0">
                  <Mail size={18} strokeWidth={1.5} className="text-ecospora-green-deep" />
                </span>
                hello@ecospora.com
              </a>
              <div className="flex items-center gap-4 text-lg text-ecospora-charcoal" data-testid="contact-location">
                <span className="w-12 h-12 rounded-full bg-ecospora-white border border-ecospora-green-deep/15 flex items-center justify-center shrink-0">
                  <MapPin size={18} strokeWidth={1.5} className="text-ecospora-green-deep" />
                </span>
                Perlis, Malaysia
              </div>
              <p className="text-ecospora-charcoal/75 leading-relaxed max-w-lg">
                We read every message. Tell us who you are and what you're building, and we'll find the right person to reply.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FORM */}
      <section id="form" className="section-y bg-ecospora-white scroll-mt-20">
        <div className="container-x">
          <Reveal className="max-w-2xl mx-auto text-center mb-12">
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-ecospora-green-deep leading-[1.05]">
              Ready to talk?
            </h2>
            <p className="mt-5 text-lg text-ecospora-charcoal/75">
              Applications are reviewed within 5 working days.
            </p>
          </Reveal>

          <Reveal className="max-w-3xl mx-auto">
            <form onSubmit={submit} className="bg-ecospora-cream rounded-2xl p-8 md:p-12 border border-ecospora-green-deep/10" data-testid="contact-form">
              <div className="grid md:grid-cols-2 gap-6">
                <Field label="Name" required value={form.name} onChange={update("name")} testid="contact-name" />
                <Field label="Company" required value={form.company} onChange={update("company")} testid="contact-company" />
                <Field label="Role" required value={form.role} onChange={update("role")} testid="contact-role" />
                <Field label="Email" type="email" required value={form.email} onChange={update("email")} testid="contact-email" />
                <Field label="Phone" value={form.phone} onChange={update("phone")} testid="contact-phone" />
                <SelectField label="Region" required value={form.region} onChange={update("region")} testid="contact-region"
                  options={["Perlis & Northern Malaysia", "Central Malaysia", "Southern Malaysia", "East Malaysia", "International"]} />
                <div className="md:col-span-2">
                  <SelectField label="How can we work together?" required value={form.track} onChange={update("track")} testid="contact-track"
                    options={["B2B Partnership", "Agency", "University", "Not sure yet"]} />
                </div>
                <div className="md:col-span-2">
                  <Textarea label="Tell us about your project" required minLength={50} value={form.business_brief} onChange={update("business_brief")} testid="contact-brief" />
                </div>
                <div className="md:col-span-2">
                  <Textarea label="Anything else we should know" value={form.other_notes} onChange={update("other_notes")} testid="contact-other" />
                </div>
              </div>
              <div className="mt-8 flex flex-col md:flex-row items-start md:items-center gap-6">
                <button type="submit" disabled={submitting} className="btn-primary disabled:opacity-60" data-testid="contact-submit">
                  {submitting ? "Submitting…" : "Send message"}
                </button>
                {success && (
                  <p className="text-sm text-ecospora-green-deep" data-testid="contact-success">
                    Thanks — we'll be in touch within 5 working days.
                  </p>
                )}
              </div>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

const Field = ({ label, required, type = "text", value, onChange, testid }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-[0.18em] text-ecospora-charcoal/60 mb-2 block">
      {label} {required && <span className="text-ecospora-ochre">*</span>}
    </span>
    <input
      type={type}
      required={required}
      value={value}
      onChange={onChange}
      data-testid={testid}
      className="w-full bg-transparent border-b border-ecospora-green-deep/25 focus:border-ecospora-green-deep py-3 outline-none transition-colors text-ecospora-charcoal placeholder:text-ecospora-charcoal/30"
    />
  </label>
);

const SelectField = ({ label, required, value, onChange, options, testid }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-[0.18em] text-ecospora-charcoal/60 mb-2 block">
      {label} {required && <span className="text-ecospora-ochre">*</span>}
    </span>
    <select
      required={required}
      value={value}
      onChange={onChange}
      data-testid={testid}
      className="w-full bg-transparent border-b border-ecospora-green-deep/25 focus:border-ecospora-green-deep py-3 outline-none transition-colors text-ecospora-charcoal"
    >
      <option value="">Select…</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </label>
);

const Textarea = ({ label, required, minLength, value, onChange, testid }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-[0.18em] text-ecospora-charcoal/60 mb-2 block">
      {label} {required && <span className="text-ecospora-ochre">*</span>}
    </span>
    <textarea
      required={required}
      minLength={minLength}
      value={value}
      onChange={onChange}
      rows={4}
      data-testid={testid}
      className="w-full bg-transparent border border-ecospora-green-deep/15 focus:border-ecospora-green-deep rounded-lg p-4 outline-none transition-colors text-ecospora-charcoal placeholder:text-ecospora-charcoal/30 resize-none"
    />
  </label>
);

export default Contact;
